// Phase 20 · Community V3 · Custom Tailwind month/week/day calendar grid.
import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Circle } from "lucide-react";

function pad(n) { return String(n).padStart(2, "0"); }

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function startOfWeek(d) {
    const c = new Date(d);
    const day = c.getDay() || 7;
    c.setDate(c.getDate() - day + 1);
    c.setHours(0, 0, 0, 0);
    return c;
}

const TYPE_COLOR = {
    live_stream: "bg-[#FAFAFA]",
    webinar: "bg-[#FFFFFF]",
    workshop: "bg-[#CCCCCC]",
    qa_ama: "bg-[#CCCCCC]",
};

export default function EventCalendarGrid({ view, cursor, onCursorChange, events }) {
    const cells = useMemo(() => {
        if (view === "month") {
            const som = startOfMonth(cursor);
            const eom = endOfMonth(cursor);
            const first = startOfWeek(som);
            const arr = [];
            const cur = new Date(first);
            for (let i = 0; i < 42; i++) {
                arr.push(new Date(cur));
                cur.setDate(cur.getDate() + 1);
            }
            return { type: "month", days: arr, som, eom };
        }
        if (view === "week") {
            const start = startOfWeek(cursor);
            const arr = [];
            const cur = new Date(start);
            for (let i = 0; i < 7; i++) {
                arr.push(new Date(cur));
                cur.setDate(cur.getDate() + 1);
            }
            return { type: "week", days: arr };
        }
        return { type: "day", days: [new Date(cursor)] };
    }, [cursor, view]);

    // Group events by yyyy-mm-dd
    const byDay = useMemo(() => {
        const map = {};
        (events || []).forEach((e) => {
            const d = new Date(e.scheduled_start);
            const k = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
            (map[k] ||= []).push(e);
        });
        return map;
    }, [events]);

    const shift = (dir) => {
        const c = new Date(cursor);
        if (view === "month") c.setMonth(c.getMonth() + dir);
        else if (view === "week") c.setDate(c.getDate() + dir * 7);
        else c.setDate(c.getDate() + dir);
        onCursorChange(c);
    };

    const label = useMemo(() => {
        if (view === "month") {
            return cursor.toLocaleDateString([], { month: "long", year: "numeric" });
        }
        if (view === "week") {
            const s = startOfWeek(cursor);
            const e = new Date(s); e.setDate(s.getDate() + 6);
            return `${s.toLocaleDateString([], { month: "short", day: "numeric" })} — ${e.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}`;
        }
        return cursor.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    }, [cursor, view]);

    return (
        <div data-testid="event-calendar-grid" className="bg-[#111111] border border-[#1A1A1A] rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#1A1A1A]">
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => shift(-1)}
                        className="w-8 h-8 rounded-lg border border-[#1A1A1A] text-neutral-400 hover:text-[#FFFFFF] hover:border-[#FFFFFF]/40 flex items-center justify-center"
                        data-testid="cal-prev"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <h2 className="text-sm font-semibold text-neutral-100 capitalize" data-testid="cal-label">
                        {label}
                    </h2>
                    <button
                        type="button"
                        onClick={() => shift(1)}
                        className="w-8 h-8 rounded-lg border border-[#1A1A1A] text-neutral-400 hover:text-[#FFFFFF] hover:border-[#FFFFFF]/40 flex items-center justify-center"
                        data-testid="cal-next"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
                <button
                    type="button"
                    onClick={() => onCursorChange(new Date())}
                    className="text-xs text-neutral-400 hover:text-[#FFFFFF]"
                    data-testid="cal-today"
                >
                    Today
                </button>
            </div>

            {view === "month" && (
                <div className="grid grid-cols-7 text-[10px] uppercase tracking-widest text-neutral-500 border-b border-[#1A1A1A]">
                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                        <div key={d} className="px-2 py-1.5 border-r border-[#1A1A1A] last:border-r-0">{d}</div>
                    ))}
                </div>
            )}
            {view === "month" && (
                <div className="grid grid-cols-7">
                    {cells.days.map((d, i) => {
                        const k = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
                        const dayEvents = byDay[k] || [];
                        const isCurrentMonth = d.getMonth() === cursor.getMonth();
                        const isToday =
                            d.toDateString() === new Date().toDateString();
                        return (
                            <div
                                key={i}
                                className={`min-h-[86px] p-1.5 border-r border-b border-[#1A1A1A] last:border-r-0 ${isCurrentMonth ? "" : "opacity-40"}`}
                                data-testid={`cal-day-${k}`}
                            >
                                <div className={`text-[11px] font-semibold mb-1 ${isToday ? "text-[#FFFFFF]" : "text-neutral-400"}`}>
                                    {d.getDate()}
                                </div>
                                <div className="space-y-1">
                                    {dayEvents.slice(0, 3).map((e) => (
                                        <Link
                                            key={e.id}
                                            to={`/events/${e.slug || e.id}`}
                                            className={`block truncate text-[10px] text-neutral-100 rounded px-1.5 py-0.5 border ${TYPE_COLOR[e.event_type] || "bg-neutral-700"}/20 border-white/5 hover:border-white/20`}
                                            title={e.title}
                                            data-testid={`cal-evt-${e.slug || e.id}`}
                                        >
                                            {new Date(e.scheduled_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · {e.title}
                                        </Link>
                                    ))}
                                    {dayEvents.length > 3 && (
                                        <p className="text-[9px] text-neutral-500 pl-1">
                                            +{dayEvents.length - 3} more
                                        </p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {view === "week" && (
                <div className="grid grid-cols-7">
                    {cells.days.map((d, i) => {
                        const k = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
                        const dayEvents = byDay[k] || [];
                        const isToday = d.toDateString() === new Date().toDateString();
                        return (
                            <div
                                key={i}
                                className="min-h-[280px] p-2 border-r border-[#1A1A1A] last:border-r-0"
                                data-testid={`cal-wday-${k}`}
                            >
                                <div className={`text-[11px] font-semibold mb-2 ${isToday ? "text-[#FFFFFF]" : "text-neutral-400"}`}>
                                    {d.toLocaleDateString([], { weekday: "short" })} · {d.getDate()}
                                </div>
                                <div className="space-y-1.5">
                                    {dayEvents.map((e) => (
                                        <Link
                                            key={e.id}
                                            to={`/events/${e.slug || e.id}`}
                                            className={`block rounded px-2 py-1.5 text-[11px] border border-white/10 hover:border-white/30 ${TYPE_COLOR[e.event_type] || "bg-neutral-700"}/25 text-neutral-100`}
                                        >
                                            <p className="font-semibold truncate">{e.title}</p>
                                            <p className="text-[10px] opacity-70 mt-0.5">
                                                {new Date(e.scheduled_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                            </p>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {view === "day" && (
                <div className="p-4 space-y-2">
                    {cells.days.map((d, i) => {
                        const k = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
                        const dayEvents = byDay[k] || [];
                        if (!dayEvents.length) {
                            return (
                                <p key={i} className="text-sm text-neutral-500 italic text-center py-6">
                                    No events scheduled
                                </p>
                            );
                        }
                        return dayEvents.map((e) => (
                            <Link
                                key={e.id}
                                to={`/events/${e.slug || e.id}`}
                                className="flex items-center gap-3 p-3 rounded-lg bg-[#0A0A0A] border border-[#1A1A1A] hover:border-[#FFFFFF]/40 transition-colors"
                                data-testid={`cal-devt-${e.slug || e.id}`}
                            >
                                <Circle size={10} className={TYPE_COLOR[e.event_type]?.replace("bg-", "text-") || "text-neutral-400"} fill="currentColor" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-neutral-100 truncate">{e.title}</p>
                                    <p className="text-[11px] text-neutral-500">
                                        {new Date(e.scheduled_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · @{e.host_username}
                                    </p>
                                </div>
                            </Link>
                        ));
                    })}
                </div>
            )}
        </div>
    );
}
